import React, { useState, useEffect, useCallback } from 'react';
import { User, Building2, Phone, MapPin, Save, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function ProfileSettings() {
  const { checkAuth } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', company_name: '', dealership_name: '', phone: '', address: ''
  });

  const fetchProfile = useCallback(async () => {
    try {
      const { data } = await api.get('/api/profile');
      setProfile(data);
      setForm({
        name: data.name || '',
        company_name: data.company_name || '',
        dealership_name: data.dealership_name || '',
        phone: data.phone || '',
        address: data.address || ''
      });
    } catch { toast.error('Failed to load profile'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put('/api/profile', form);
      setProfile(data);
      toast.success('Profile updated');
      // Refresh auth context so sidebar shows updated name
      await checkAuth();
    } catch { toast.error('Failed to save profile'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="animate-pulse h-64 bg-gray-100 rounded-2xl" />;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm" data-testid="profile-settings">
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg">
            <User className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-xl">Profile Settings</h3>
            <p className="text-gray-500 text-sm">Customize your name, company details, and contact info</p>
          </div>
        </div>
      </div>
      <form onSubmit={handleSave} className="p-6 space-y-5">
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label className="text-gray-700 font-medium flex items-center gap-2"><User className="w-4 h-4" /> Display Name</Label>
            <Input
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="Your name"
              className="mt-2 h-12 rounded-xl"
              data-testid="profile-name-input"
            />
          </div>
          <div>
            <Label className="text-gray-700 font-medium flex items-center gap-2"><Building2 className="w-4 h-4" /> Company Name</Label>
            <Input
              value={form.company_name}
              onChange={e => setForm({ ...form, company_name: e.target.value })}
              placeholder="Your company name (shown on invoices)"
              className="mt-2 h-12 rounded-xl"
              data-testid="profile-company-input"
            />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label className="text-gray-700 font-medium flex items-center gap-2"><Building2 className="w-4 h-4" /> Dealership Name</Label>
            <Input
              value={form.dealership_name}
              onChange={e => setForm({ ...form, dealership_name: e.target.value })}
              placeholder="Dealership business name"
              className="mt-2 h-12 rounded-xl"
              data-testid="profile-dealership-input"
            />
          </div>
          <div>
            <Label className="text-gray-700 font-medium flex items-center gap-2"><Phone className="w-4 h-4" /> Phone Number</Label>
            <Input
              value={form.phone}
              onChange={e => setForm({ ...form, phone: e.target.value })}
              placeholder="(555) 123-4567"
              className="mt-2 h-12 rounded-xl"
              data-testid="profile-phone-input"
            />
          </div>
        </div>
        <div>
          <Label className="text-gray-700 font-medium flex items-center gap-2"><MapPin className="w-4 h-4" /> Address</Label>
          <Input
            value={form.address}
            onChange={e => setForm({ ...form, address: e.target.value })}
            placeholder="Business address"
            className="mt-2 h-12 rounded-xl"
            data-testid="profile-address-input"
          />
        </div>
        <div className="flex items-center gap-3 pt-2">
          <Button type="submit" disabled={saving} className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl" data-testid="profile-save-btn">
            {saving ? <><Save className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Check className="w-4 h-4 mr-2" /> Save Changes</>}
          </Button>
          <span className="text-sm text-gray-400">Last updated: {profile?.updated_at ? new Date(profile.updated_at).toLocaleDateString() : 'Never'}</span>
        </div>
      </form>
    </div>
  );
}
